"use client"
import Image from "next/image"
import { HttpTypes } from "@medusajs/types"
import { EmblaCarouselType } from "embla-carousel"
import { useCallback, useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { Indicator } from "@/components/atoms"
import useEmblaCarousel from "embla-carousel-react"
import { useTranslate } from "@tolgee/react"
import { MediaPlay, PencilSquare, ThumbnailBadge } from "@medusajs/icons"

export const ProductCarouselIndicator = ({
  slides = [],
  embla: parentEmbla,
  scrollToEnd
}: {
  slides: HttpTypes.StoreProduct["images"]
  embla?: EmblaCarouselType
  scrollToEnd?: (idx: number) => void
}) => {
  const { t } = useTranslate()
  const [selectedIndex, setSelectedIndex] = useState(0)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: "y",
    loop: true,
    align: "start",
  })

  const changeSlideHandler = useCallback(
    (index: number) => {
      scrollToEnd?.(index)
      if (!parentEmbla) return
      parentEmbla.scrollTo(index)
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [parentEmbla, emblaApi]
  )

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    changeSlideHandler(0);
  }, [slides])

  useEffect(() => {
    if (!parentEmbla) return

    onSelect(parentEmbla)
    parentEmbla.on("reInit", onSelect).on("select", onSelect)
  }, [parentEmbla, onSelect])

  return (
    <>
      {/* Mobile indicator - absolute positioned at bottom */}
      <div className="md:hidden absolute bottom-4 left-4 right-4 z-10">
        <Indicator
          step={selectedIndex + 1}
          size="large"
          maxStep={slides?.length || 0}
        />
      </div>

      {/* Desktop thumbnails - positioned to the left via parent flex */}
      <div className="embla hidden md:block w-[49px] xl:w-[71px] 2xl:w-[96px] flex-shrink-0">
        <div
          className="embla__viewport overflow-hidden"
          ref={emblaRef}
        >
          <div className="embla__container max-h-[682px] space-y-2 xl:space-y-3">
            {(slides || []).map((slide, index) => (
              <div
                key={slide.id}
                className="rounded-lg cursor-pointer w-[49px] h-[49px] xl:w-[71px] xl:h-[71px] 2xl:w-[96px] 2xl:h-[96px] overflow-hidden flex-shrink-0"
                onClick={() => changeSlideHandler(index)}
              >
                {slide.url.endsWith(".mp4") ?
                  <div className={cn("size-full transition-all duration-200 rounded-lg flex justify-center items-center border-2 bg-gray-100",
                    selectedIndex === index
                      ? "border-black"
                      : "border-gray-200 hover:border-gray-300")}>
                    <MediaPlay className="scale-50 xl:scale-75 2xl:scale-100" />
                  </div>
                  : <Image
                    src={decodeURIComponent(slide.url)}
                    alt={t("product_carousel_indicator.image_alt")}
                    width={96}
                    height={96}
                    className={cn(
                      "rounded-lg border-2 transition-all duration-200 w-full h-full object-cover",
                      selectedIndex === index
                        ? "border-black"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                  />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
